import {
	useAuth,
	useClerk,
	useOrganizationList,
	useUser,
} from '@clerk/clerk-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useCompleteHubSignup } from '@/lib/api/signupProfile'
import { writeAcquisitionToUser } from '@/hooks/use-acquisition-tracking'
import { clearCapturedPartner, resolveSignupPartner } from '@/lib/utils/captured-partner'
import { getStoredAcquisition, hasAcquisitionData } from '@/lib/utils/acquisition-tracking'
import { capture } from '@tagadapay/analytics'
import { useSignupPersistence } from '@/app/sign-up-new/hooks/use-signup-persistence'
import { buildSignupProfileInput, buildAccountName, hasValidSignupData } from '@/app/sign-up-new/utils'
import { partnerOrigins } from '@/config/clerk-satellite'
import { currentPartnerBrand } from '@/config/current-partner'
import { getPostAuthOrgPath } from '@/hooks/use-privileged-staff-bypass'
import { trackXSignup } from '@/lib/analytics/x-pixel'
import { setGaIdentityFromSignup, trackGaSignUp } from '@/lib/analytics/ga'

type EmailUser = {
	id?: string
	firstName?: string | null
	lastName?: string | null
	primaryEmailAddress?: { emailAddress: string } | null
	emailAddresses?: { emailAddress: string }[]
} | null | undefined

const primaryEmail = (user: EmailUser): string | null =>
	user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? null

/**
 * Read and consume the partner redirect URL persisted by CustomSignIn
 * before the OAuth round-trip. Validates the origin against the partner
 * allowlist before returning it. Returns null if absent or invalid.
 */
function consumePartnerRedirectUrl(): string | null {
	if (typeof window === 'undefined') return null
	const raw = sessionStorage.getItem('partner_redirect_url')
	if (!raw) return null
	sessionStorage.removeItem('partner_redirect_url')
	try {
		const url = new URL(raw)
		if (partnerOrigins().includes(url.origin)) return url.toString()
	} catch {
		return null
	}
	return null
}

const goPartnerRedirect = (
	clerk: { buildUrlWithAuth: (url: string) => string },
	url: string,
): void => {
	try {
		window.location.replace(clerk.buildUrlWithAuth(url))
	} catch {
		window.location.replace(url)
	}
}

const continueNewSignup = async (
	user: EmailUser,
	hasWizardData: boolean,
	clear: () => void,
	navigate: (path: string) => void,
): Promise<void> => {
	await trackXSignup({
		email: primaryEmail(user),
		conversionId: user?.id,
	})
	setGaIdentityFromSignup({
		firstName: user?.firstName,
		lastName: user?.lastName,
		email: primaryEmail(user),
		userId: user?.id,
	})
	trackGaSignUp({ method: 'google' })
	if (!hasWizardData) {
		navigate('/sign-up-new?mode=sso')
		return
	}
	localStorage.setItem('signup_mode', 'sso')
	clear()
	navigate('/')
}

const LoadingScreen = ({ message }: { message: string }) => (
	<div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
		<div className="text-center">
			<Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
			<h2 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
				{message}
			</h2>
		</div>
	</div>
)

/**
 * Handles post-SSO sign-in: checks whether the user has an org.
 * If no org and no wizard data in sessionStorage, redirects to /sign-up-new.
 * New Google users from /sign-up land here (no org yet) — fire Sign-up then.
 */
function SSOSigninRedirect() {
	const navigate = useNavigate()
	const { isSignedIn, isLoaded: authLoaded } = useAuth()
	const { user, isLoaded: userLoaded } = useUser()
	const clerk = useClerk()
	const { userMemberships, isLoaded: membershipsLoaded } = useOrganizationList({
		userMemberships: { pageSize: 1 },
	})
	const { load, clear } = useSignupPersistence()
	const attemptedRef = useRef(false)

	useEffect(() => {
		if (!authLoaded || !membershipsLoaded || !userLoaded || !isSignedIn) return
		if (attemptedRef.current) return
		attemptedRef.current = true

		const finish = async () => {
			// Partner satellite: handshake redirect (see consumePartnerRedirectUrl).
			const partnerRedirectUrl = consumePartnerRedirectUrl()
			if (partnerRedirectUrl) {
				goPartnerRedirect(clerk, partnerRedirectUrl)
				return
			}
			if ((userMemberships?.data?.length ?? 0) > 0) {
				navigate('/')
				return
			}
			// No org yet (e.g. Google from /sign-up) → Sign-up; deduped if wizard later.
			await continueNewSignup(user, hasValidSignupData(load()), clear, navigate)
		}

		void finish()
	}, [
		authLoaded,
		membershipsLoaded,
		userLoaded,
		isSignedIn,
		userMemberships,
		user,
		navigate,
		load,
		clear,
		clerk,
	])

	return <LoadingScreen message="Redirecting..." />
}

/**
 * Handles post-SSO profile creation: reads the wizard form data from
 * sessionStorage, calls completeHubSignup, sets the active org, then navigates.
 */
function SSOSignupComplete() {
	const navigate = useNavigate()
	const { isSignedIn, isLoaded } = useAuth()
	const { user } = useUser()
	const { setActive } = useClerk()
	const { load, clear } = useSignupPersistence()
	const { mutateAsync } = useCompleteHubSignup()
	const [error, setError] = useState('')
	const attemptedRef = useRef(false)

	useEffect(() => {
		if (!isLoaded || !isSignedIn || !user || attemptedRef.current) return
		attemptedRef.current = true

		const formData = load()

		if (!hasValidSignupData(formData)) {
			navigate('/')
			return
		}

		const completeSignup = async () => {
			try {
				// Acquisition normally rides authenticateWithRedirect's unsafeMetadata
				// (TAG-2514); this is the backstop for older tabs, written BEFORE the
				// org is created so the CRM admin sees it at org creation.
				await writeAcquisitionToUser(user)
				// Host-based partner mode, else the `?partner=` deep-link cookie.
				const partner = resolveSignupPartner(currentPartnerBrand())
				const res = await mutateAsync({
					accountName: buildAccountName(formData),
					signupProfile: buildSignupProfileInput(formData),
					partner,
				})
				await setActive({ organization: res.orgId })
				clearCapturedPartner()
				capture('account_created', {
					method: 'google',
					partner_slug: partner,
					has_acquisition: hasAcquisitionData(getStoredAcquisition()),
				})
				await trackXSignup({
					email: formData.aboutYou.email,
					conversionId: res.orgId,
				})
				setGaIdentityFromSignup({
					firstName: formData.aboutYou.firstName,
					lastName: formData.aboutYou.lastName,
					email: formData.aboutYou.email,
					accountName: buildAccountName(formData),
					organization: buildAccountName(formData),
					userId: user?.id,
				})
				trackGaSignUp({ method: 'google' })
				clear()
				// Full load: a client navigate keeps the shell's membership list fetched before the org existed, which loops through /no-organization.
				window.location.assign(getPostAuthOrgPath(res.orgSlug, false))
			} catch {
				setError('Failed to complete signup. Redirecting...')
				clear()
				setTimeout(() => navigate('/'), 3000)
			}
		}

		completeSignup()
	}, [isLoaded, isSignedIn, user, load, clear, mutateAsync, navigate, setActive])

	if (error) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
				<div className="text-center">
					<p className="text-sm text-red-600 dark:text-red-400">{error}</p>
				</div>
			</div>
		)
	}

	return <LoadingScreen message="Setting up your account..." />
}

/**
 * Processes the OAuth redirect callback manually instead of using
 * AuthenticateWithRedirectCallback to prevent React StrictMode
 * from calling handleRedirectCallback twice and creating duplicate users.
 */
function SSOCallbackProcessor() {
	const clerk = useClerk()
	const processedRef = useRef(false)

	useEffect(() => {
		if (!clerk.loaded || processedRef.current) return
		processedRef.current = true

		clerk.handleRedirectCallback({
			signInForceRedirectUrl: '/sso-callback?signin=1',
			signUpForceRedirectUrl: '/sso-callback?signin=1',
		})
	}, [clerk])

	return null
}

/**
 * SSO callback page with three modes:
 * 1. No query params → processes OAuth tokens via Clerk (guarded against double-execution)
 * 2. ?complete=1 → post-signup profile creation (from wizard Google sign-up)
 * 3. ?signin=1 → post-sign-in check (redirects to /sign-up-new if no org)
 */
export default function SSOCallback() {
	const [searchParams] = useSearchParams()
	const isSignupComplete = searchParams.get('complete') === '1'
	const isSigninCallback = searchParams.get('signin') === '1'

	if (isSignupComplete) {
		return <SSOSignupComplete />
	}

	if (isSigninCallback) {
		return <SSOSigninRedirect />
	}

	return (
		<>
			<SSOCallbackProcessor />
			<LoadingScreen message="Completing sign in..." />
		</>
	)
}
